// src/components/ResumeDataAdapter.js
/**
 * Utility functions to adapt form data to the format expected by resume templates
 */

/**
 * Adapts form data or cloud function data for resume templates
 * @param {Object} formData - The form data from FormContext or cloud function
 * @returns {Object} - Formatted resume data with proper field mapping and structure
 */
export const adaptFormDataForTemplates = (formData) => {
  if (!formData) return {};

  // Create a copy of the form data to avoid mutating the original
  const adaptedData = { ...formData };

  // Handle field name mapping for social links
  if (adaptedData.personalInformation) {
    const pi = adaptedData.personalInformation;

    // Map linkedinUrl to linkedin if needed
    if (pi.linkedinUrl && !pi.linkedin) {
      pi.linkedin = pi.linkedinUrl;
    } else if (pi.linkedin && !pi.linkedinUrl) {
      pi.linkedinUrl = pi.linkedin;
    }

    // Map githubUrl to github if needed
    if (pi.githubUrl && !pi.github) {
      pi.github = pi.githubUrl;
    } else if (pi.github && !pi.githubUrl) {
      pi.githubUrl = pi.github;
    }

    // Map portfolioUrl to portfolio if needed
    if (pi.portfolioUrl && !pi.portfolio) {
      pi.portfolio = pi.portfolioUrl;
    } else if (pi.portfolio && !pi.portfolioUrl) {
      pi.portfolioUrl = pi.portfolio;
    }

    // Map address to location if needed
    if (pi.address && !pi.location) {
      pi.location = pi.address;
    } else if (pi.location && !pi.address) {
      pi.address = pi.location;
    }

    // Ensure professionalSummary is available in both locations
    if (
      pi.professionalSummary &&
      (!adaptedData.targetJobInformation ||
        !adaptedData.targetJobInformation.professionalSummary)
    ) {
      if (!adaptedData.targetJobInformation) {
        adaptedData.targetJobInformation = {};
      }
      adaptedData.targetJobInformation.professionalSummary =
        pi.professionalSummary;
    } else if (
      adaptedData.targetJobInformation &&
      adaptedData.targetJobInformation.professionalSummary &&
      !pi.professionalSummary
    ) {
      pi.professionalSummary =
        adaptedData.targetJobInformation.professionalSummary;
    }
  }

  // Check if skills are already provided directly in the resumeData
  // This preserves skills that come from the backend
  if (!adaptedData.skills || adaptedData.skills.length === 0) {
    // If no skills are directly provided, try to extract them from additionalQualifications
    const extractedSkills = extractSkillsFromQualifications(
      formData.additionalQualifications
    );

    // Only set extracted skills if they exist
    if (extractedSkills && extractedSkills.length > 0) {
      adaptedData.skills = extractedSkills;
    }
  }

  // Handle work experience formatting
  if (adaptedData.workExperience && Array.isArray(adaptedData.workExperience)) {
    adaptedData.workExperience = adaptedData.workExperience.map((job) => {
      // Ensure company name is available in both fields
      if (job.companyName && !job.company) {
        job.company = job.companyName;
      } else if (job.company && !job.companyName) {
        job.companyName = job.company;
      }

      // Handle special date format from cloud function
      if (job.endDate === "9999-01-01") {
        job.endDate = "Present";
      }

      return job;
    });
  }

  // Log the adapted data for debugging
  console.log("Adapted Resume Data:", adaptedData);
  console.log("Skills:", adaptedData.skills);

  return adaptedData;
};

/**
 * Extracts skills from additionalQualifications
 * @param {Array} additionalQualifications - The additionalQualifications array from form data
 * @returns {Array} - Array of skill strings
 */
const extractSkillsFromQualifications = (additionalQualifications = []) => {
  // Initialize skills array
  let skills = [];

  // If additionalQualifications is not an array or is empty, return empty array
  if (
    !Array.isArray(additionalQualifications) ||
    additionalQualifications.length === 0
  ) {
    return skills;
  }

  // Look for skills in various qualification types
  additionalQualifications.forEach((qualification) => {
    if (!qualification || !qualification.details) return;

    const { qualificationType, details } = qualification;

    // Extract skills from Personal Project qualifications
    if (qualificationType === "Personal Project" && details.skills) {
      if (Array.isArray(details.skills)) {
        skills = [...skills, ...details.skills];
      } else if (typeof details.skills === "string") {
        // If skills is a string, split by commas
        const skillsArray = details.skills
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        skills = [...skills, ...skillsArray];
      }
    }

    // Extract skills from Volunteer Experience qualifications
    if (
      (qualificationType === "Volunteer Experience" ||
        qualificationType === "Volunteering") &&
      details.skills
    ) {
      if (Array.isArray(details.skills)) {
        skills = [...skills, ...details.skills];
      } else if (typeof details.skills === "string") {
        const skillsArray = details.skills
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        skills = [...skills, ...skillsArray];
      }
    }

    // Extract skills from Research Experience qualifications
    if (qualificationType === "Research Experience") {
      // Research skills might be mentioned in description or outcomes
      const descriptionSkills = extractSkillsFromText(details.description);
      const outcomesSkills = extractSkillsFromText(details.outcomes);
      skills = [...skills, ...descriptionSkills, ...outcomesSkills];
    }

    // Extract skills from Military Service qualifications
    if (qualificationType === "Military Service" && details.specialTraining) {
      const trainingSkills = extractSkillsFromText(details.specialTraining);
      skills = [...skills, ...trainingSkills];
    }
  });

  // Remove duplicates and return
  return [...new Set(skills)];
};

/**
 * Extracts potential skills from text by looking for comma-separated items
 * @param {string} text - Text to extract skills from
 * @returns {Array} - Array of potential skills
 */
const extractSkillsFromText = (text = "") => {
  if (!text || typeof text !== "string") return [];

  // Simple extraction - split by commas and clean up
  return text
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

export default adaptFormDataForTemplates;
